import React, { useState, useEffect, useMemo } from 'react';
import {
    Modal,
    Box,
    Typography,
    TextField,
    Grid,
    Card,
    CardContent,
    CardActions,
    Button,
    Chip,
    IconButton,
    InputAdornment,
    CircularProgress,
    Alert,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Collapse
} from '@mui/material';
import { Close, Build, Search, ExpandMore, ExpandLess } from '@mui/icons-material';
import axios from 'axios';
import { API_PATH } from '../../../App';
import { getAxiosConfig } from '../../../helpers/AuthHelper';
import { EmitterMismatchSummary, WorkerHashrateMismatch } from '../Monitor/types';

interface HashrateMismatchModalProps {
    open: boolean;
    onClose: () => void;
    onRefresh?: () => void;
}

const HashrateMismatchModal: React.FC<HashrateMismatchModalProps> = ({ open, onClose, onRefresh }) => {
    const [mismatchData, setMismatchData] = useState<EmitterMismatchSummary[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [selectedEmitter, setSelectedEmitter] = useState<string>('all');
    const [refreshing, setRefreshing] = useState<string | null>(null);
    const [expandedEmitters, setExpandedEmitters] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (open) {
            fetchMismatchData();
        }
    }, [open]);

    const fetchMismatchData = () => {
        setLoading(true);
        axios.get(API_PATH + '/monitor/hashrate-mismatch', getAxiosConfig())
            .then(response => {
                setMismatchData(response.data);
                setLoading(false);
            })
            .catch(error => {
                console.error('Error fetching mismatch data:', error);
                setLoading(false);
            });
    };

    const handleFixWorker = (workerId: string) => {
        setRefreshing(workerId);
        // Refresh the data after a short delay
        setTimeout(() => {
            fetchMismatchData();
            setRefreshing(null);
            if (onRefresh) {
                onRefresh();
            }
        }, 1000);
    };

    const toggleEmitter = (emitterName: string) => {
        setExpandedEmitters(prev => {
            const newSet = new Set(prev);
            if (newSet.has(emitterName)) {
                newSet.delete(emitterName);
            } else {
                newSet.add(emitterName);
            }
            return newSet;
        });
    };

    // Get unique emitter names for filter
    const emitterNames = useMemo(() => {
        return mismatchData.map(e => e.emitterName).sort();
    }, [mismatchData]);

    // Filter mismatches based on search query and selected emitter
    const filteredData = useMemo(() => {
        let data = mismatchData;

        // Filter by emitter
        if (selectedEmitter !== 'all') {
            data = data.filter(emitter => emitter.emitterName === selectedEmitter);
        }

        // Filter by search query
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            data = data
                .map(emitter => ({
                    ...emitter,
                    mismatchedWorkers: emitter.mismatchedWorkers.filter(
                        worker =>
                            worker.workerId.toLowerCase().includes(query) ||
                            worker.machineModelName.toLowerCase().includes(query)
                    )
                }))
                .filter(emitter => emitter.mismatchedWorkers.length > 0);
        }

        return data;
    }, [mismatchData, searchQuery, selectedEmitter]);

    const getBorderColor = (variancePercentage: number) => {
        if (variancePercentage > 20) return '#f44336'; // Red
        if (variancePercentage > 10) return '#ff9800'; // Orange
        return '#4caf50'; // Green
    };

    const totalMismatches = mismatchData.reduce((sum, emitter) => sum + emitter.mismatchedWorkers.length, 0);

    return (
        <Modal open={open} onClose={onClose}>
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '95%', sm: '90%', md: '85%', lg: '1200px' },
                    maxHeight: '90vh',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    boxShadow: 24,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        p: 3,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white'
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                            <Typography variant="h5" fontWeight="bold">
                                Hashrate Mismatches
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                                {totalMismatches} worker{totalMismatches !== 1 ? 's' : ''} with hashrate discrepancies
                            </Typography>
                        </Box>
                        <IconButton onClick={onClose} sx={{ color: 'white' }}>
                            <Close />
                        </IconButton>
                    </Box>

                    {/* Filters */}
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={8}>
                            <TextField
                                fullWidth
                                placeholder="Search by worker ID or machine model..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                sx={{
                                    bgcolor: 'rgba(255, 255, 255, 0.15)',
                                    borderRadius: 1,
                                    '& .MuiOutlinedInput-root': {
                                        color: 'white',
                                        '& fieldset': {
                                            borderColor: 'rgba(255, 255, 255, 0.3)',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: 'rgba(255, 255, 255, 0.5)',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: 'white',
                                        },
                                    },
                                    '& .MuiInputBase-input::placeholder': {
                                        color: 'rgba(255, 255, 255, 0.7)',
                                        opacity: 1,
                                    },
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth sx={{
                                bgcolor: 'rgba(255, 255, 255, 0.15)',
                                borderRadius: 1,
                                '& .MuiOutlinedInput-root': {
                                    color: 'white',
                                    '& fieldset': {
                                        borderColor: 'rgba(255, 255, 255, 0.3)',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'rgba(255, 255, 255, 0.5)',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'white',
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    color: 'rgba(255, 255, 255, 0.7)',
                                },
                                '& .MuiSvgIcon-root': {
                                    color: 'rgba(255, 255, 255, 0.7)',
                                },
                            }}>
                                <InputLabel>Emitter</InputLabel>
                                <Select
                                    value={selectedEmitter}
                                    onChange={(e) => setSelectedEmitter(e.target.value)}
                                    label="Emitter"
                                >
                                    <MenuItem value="all">All Emitters</MenuItem>
                                    {emitterNames.map((name) => (
                                        <MenuItem key={name} value={name}>{name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Box>

                {/* Content */}
                <Box sx={{ p: 3, overflow: 'auto', flexGrow: 1 }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                            <CircularProgress />
                        </Box>
                    ) : filteredData.length === 0 ? (
                        <Alert severity="success" sx={{ mt: 2 }}>
                            {searchQuery ? 'No mismatches found matching your search.' : 'No hashrate mismatches detected!'}
                        </Alert>
                    ) : (
                        filteredData.map((emitter, index) => {
                            const isExpanded = expandedEmitters.has(emitter.emitterName);
                            const totalVariance = emitter.mismatchedWorkers.reduce((sum, w) => sum + Math.abs(w.variance ?? 0), 0);
                            const avgVariancePercent = emitter.mismatchedWorkers.reduce((sum, w) => sum + (w.variancePercentage ?? 0), 0) / emitter.mismatchedWorkers.length;

                            return (
                                <Box key={index} sx={{ mb: 3 }}>
                                    {/* Collapsible Header */}
                                    <Box
                                        onClick={() => toggleEmitter(emitter.emitterName)}
                                        sx={{
                                            p: 2.5,
                                            background: 'linear-gradient(135deg, rgba(255, 87, 34, 0.05) 0%, rgba(244, 67, 54, 0.05) 100%)',
                                            borderLeft: '4px solid',
                                            borderLeftColor: '#ff5722',
                                            borderRadius: 2,
                                            cursor: 'pointer',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                            '&:hover': {
                                                boxShadow: '0 4px 16px rgba(255, 87, 34, 0.2)',
                                                transform: 'translateX(4px)',
                                                background: 'linear-gradient(135deg, rgba(255, 87, 34, 0.08) 0%, rgba(244, 67, 54, 0.08) 100%)',
                                            },
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                                {emitter.emitterName}
                                            </Typography>
                                            <Chip
                                                label={`${emitter.mismatchedWorkers.length} / ${emitter.totalWorkers} mismatched`}
                                                size="small"
                                                sx={{
                                                    bgcolor: '#ff5722',
                                                    color: 'white',
                                                    fontWeight: 600,
                                                }}
                                            />
                                            <Chip
                                                label={`Avg: ${avgVariancePercent.toFixed(1)}%`}
                                                size="small"
                                                sx={{
                                                    bgcolor: 'rgba(255, 152, 0, 0.1)',
                                                    color: '#f57c00',
                                                    border: '1px solid rgba(255, 152, 0, 0.3)',
                                                    fontWeight: 500,
                                                }}
                                            />
                                            <Chip
                                                label={`Total: ${totalVariance.toFixed(2)} TH/s`}
                                                size="small"
                                                sx={{
                                                    bgcolor: 'rgba(255, 152, 0, 0.1)',
                                                    color: '#f57c00',
                                                    border: '1px solid rgba(255, 152, 0, 0.3)',
                                                    fontWeight: 500,
                                                }}
                                            />
                                        </Box>
                                        <IconButton
                                            size="small"
                                            sx={{
                                                color: '#ff5722',
                                                transition: 'transform 0.3s',
                                                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                            }}
                                        >
                                            <ExpandMore />
                                        </IconButton>
                                    </Box>

                                    {/* Collapsible Content */}
                                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                        <Box sx={{ mt: 2 }}>
                                            <Grid container spacing={2}>
                                                {emitter.mismatchedWorkers.map((worker, workerIndex) => (
                                                    <Grid item xs={12} sm={6} md={4} key={workerIndex}>
                                                        <Card
                                                            elevation={0}
                                                            sx={{
                                                                borderRadius: 2,
                                                                border: '1px solid',
                                                                borderColor: 'divider',
                                                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                                position: 'relative',
                                                                overflow: 'visible',
                                                                '&:before': {
                                                                    content: '""',
                                                                    position: 'absolute',
                                                                    top: 0,
                                                                    left: 0,
                                                                    right: 0,
                                                                    height: '4px',
                                                                    background: 'linear-gradient(90deg, #ff5722 0%, #f44336 100%)',
                                                                    borderRadius: '8px 8px 0 0',
                                                                },
                                                                '&:hover': {
                                                                    transform: 'translateY(-8px)',
                                                                    boxShadow: '0 12px 24px rgba(255, 87, 34, 0.15)',
                                                                    borderColor: '#ff5722',
                                                                },
                                                            }}
                                                        >
                                                            <CardContent>
                                                                <Typography variant="h6" component="div" gutterBottom fontWeight="bold">
                                                                    {worker.workerId}
                                                                </Typography>
                                                                <Chip
                                                                    label={worker.machineModelName}
                                                                    size="small"
                                                                    color="primary"
                                                                    variant="outlined"
                                                                    sx={{ mb: 2 }}
                                                                />
                                                                <Box sx={{ mt: 2 }}>
                                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                                        <strong>Machine Model Hashrate:</strong> {worker.machineModelHashrate?.toFixed(2) ?? 'N/A'} TH/s
                                                                    </Typography>
                                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                                        <strong>Hashrate Set On NiceHash:</strong> {worker.hashrateSetOnNiceHash?.toFixed(2) ?? 'N/A'} TH/s
                                                                    </Typography>
                                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                                        <strong>Hashrate Received From Nicehash:</strong> {worker.hashrateReceivedFromNicehash?.toFixed(2) ?? 'N/A'} TH/s
                                                                    </Typography>
                                                                    <Box
                                                                        sx={{
                                                                            mt: 1.5,
                                                                            p: 1.5,
                                                                            borderRadius: 1,
                                                                            background: 'linear-gradient(135deg, rgba(255, 87, 34, 0.08) 0%, rgba(244, 67, 54, 0.08) 100%)',
                                                                            border: '1px solid rgba(255, 87, 34, 0.2)',
                                                                        }}
                                                                    >
                                                                        <Typography
                                                                            variant="body2"
                                                                            sx={{
                                                                                color: '#d32f2f',
                                                                                fontWeight: 700,
                                                                                fontSize: '0.875rem',
                                                                            }}
                                                                        >
                                                                            Variance: {(worker.variance ?? 0) > 0 ? '+' : ''}
                                                                            {worker.variance?.toFixed(2) ?? 'N/A'} TH/s ({worker.variancePercentage?.toFixed(1) ?? 'N/A'}%)
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            </CardContent>
                                                            <CardActions>
                                                                <Button
                                                                    size="small"
                                                                    variant="contained"
                                                                    color="primary"
                                                                    startIcon={refreshing === worker.workerId ? <CircularProgress size={16} color="inherit" /> : <Build />}
                                                                    onClick={() => handleFixWorker(worker.workerId)}
                                                                    disabled={refreshing === worker.workerId}
                                                                    fullWidth
                                                                >
                                                                    {refreshing === worker.workerId ? 'Refreshing...' : 'Fix'}
                                                                </Button>
                                                            </CardActions>
                                                        </Card>
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        </Box>
                                    </Collapse>
                                </Box>
                            );
                        })
                    )}
                </Box>
            </Box>
        </Modal>
    );
};

export default HashrateMismatchModal;
